(() => {
  'use strict';
  if (customElements.get('bellomo-cart-coordinates')) return;

  class BellomoCartCoordinates extends HTMLElement {
    static pending = false;

    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => this.stepQuantity(event), options);
      this.addEventListener('input', event => {
        const item = event.target.closest('[data-coordinate-item]');
        if (item) this.updateLabel(item);
      }, options);
      this.addEventListener('submit', event => this.add(event), options);
      this.querySelectorAll('[data-coordinate-item]').forEach(item => this.updateLabel(item));
    }

    disconnectedCallback() {
      this.events?.abort();
    }

    updateLabel(item) {
      const input = item.querySelector('[data-coordinate-quantity]');
      const value = Number(input.value);
      let label = this.dataset.addLabel.replace('[quantity]', input.value || '—');
      if (label.includes('[total]')) {
        const total = Number.isFinite(value) ? Number(item.dataset.unitPrice) * value / 100 : 0;
        const formatted = new Intl.NumberFormat(document.documentElement.lang || 'it', {
          style: 'currency', currency: this.dataset.currency || 'EUR'
        }).format(total);
        label = label.replace('[total]', formatted);
      }
      item.querySelector('[data-coordinate-label]').textContent = label;
      item.querySelector('[data-coordinate-step="minus"]').disabled = value <= Number(input.min);
      item.querySelector('[data-coordinate-step="plus"]').disabled = input.max !== '' && value + Number(input.step) > Number(input.max);
    }

    stepQuantity(event) {
      const button = event.target.closest('[data-coordinate-step]');
      if (!button || BellomoCartCoordinates.pending) return;
      const item = button.closest('[data-coordinate-item]');
      const input = item.querySelector('[data-coordinate-quantity]');
      if (button.dataset.coordinateStep === 'plus') input.stepUp();
      else input.stepDown();
      this.updateLabel(item);
    }

    renderSections(sections, drawer) {
      const parse = (name, selector) => {
        if (typeof sections?.[name] !== 'string') return null;
        return new DOMParser().parseFromString(sections[name], 'text/html').querySelector(selector);
      };
      const content = parse('cart-drawer', '.js-cart-drawer-wrapper');
      const header = parse('header-cart-status', '.shopify-section');
      const main = document.querySelector('#mainCartContainer');
      const mainContent = main ? parse('main-cart', '#mainCartContainer') : null;
      const form = drawer.querySelector('sht-cart-drwr-frm');
      const badge = document.querySelector('#headerCartStatus');
      if (!content || !header || !badge || !form?.renderContents || (main && !mainContent)) return false;
      form.renderContents({ sections });
      badge.innerHTML = header.innerHTML;
      if (main && mainContent) main.innerHTML = mainContent.innerHTML;
      if (drawer.hasAttribute('open')) {
        window.SHTHelper?.trapFocus?.(drawer);
        const focus = drawer.querySelector('[data-coordinate-add], .js-cart-drawer-btn-close');
        focus?.focus({ preventScroll: true });
      }
      return true;
    }

    async refreshSections(drawer, names) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', names.join(','));
      const response = await fetch(url.pathname + url.search, { headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      return this.renderSections(await response.json(), drawer);
    }

    async add(event) {
      event.preventDefault();
      const form = event.target.closest('[data-coordinate-form]');
      if (!form || form.dataset.coordinateUncertain === 'true' || BellomoCartCoordinates.pending || !form.reportValidity()) return;
      const item = form.closest('[data-coordinate-item]');
      const input = item.querySelector('[data-coordinate-quantity]');
      const quantity = Number(input.value);
      const id = Number(item.dataset.variantId);
      const offer = this.closest('bellomo-coordinate-offer');
      const drawer = this.closest('sht-cart-drwr') || (offer && document.querySelector('sht-cart-drwr'));
      const button = item.querySelector('[data-coordinate-add]');
      const error = item.querySelector('[data-coordinate-error]');
      if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity < 1 || !drawer) return;
      if (drawer.querySelector('.js-cart-drawer-btn:disabled')) return;
      if (!drawer.querySelector('sht-cart-drwr-frm')?.renderContents) {
        error.hidden = false;
        error.textContent = this.dataset.errorLabel;
        return;
      }
      const names = ['cart-drawer', 'header-cart-status'];
      if (document.querySelector('#mainCartContainer')) names.push('main-cart');
      const controls = Array.from(drawer.querySelectorAll('button:not(.js-drawer-btn-close), input, select, textarea'));
      if (offer) controls.push(...this.querySelectorAll('button, input'));
      const states = controls.map(control => [control, control.disabled]);
      BellomoCartCoordinates.pending = true;
      controls.forEach(control => { control.disabled = true; });
      button.setAttribute('aria-busy', 'true');
      item.querySelector('[data-coordinate-label]').textContent = this.dataset.loadingLabel;
      error.hidden = true;
      let confirmed = false;
      let rendered = false;
      let uncertain = false;
      let message = this.dataset.errorLabel;
      try {
        const response = await fetch(window.routes?.cart_add_url || window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity }], sections: names, sections_url: window.location.pathname + window.location.search })
        });
        const result = await response.json();
        if (!response.ok || result.status || result.errors) {
          if (typeof result.description === 'string') message = result.description;
          rendered = await this.refreshSections(drawer, names);
        } else {
          confirmed = true;
          rendered = this.renderSections(result.sections, drawer);
          if (!rendered) rendered = await this.refreshSections(drawer, names);
        }
      } catch {
        uncertain = true;
        try { rendered = await this.refreshSections(drawer, names); } catch { rendered = false; }
      } finally {
        BellomoCartCoordinates.pending = false;
        states.forEach(([control, disabled]) => { if (control.isConnected) control.disabled = disabled; });
        button.removeAttribute('aria-busy');
        if (this.isConnected) {
          this.updateLabel(item);
          if (!rendered) {
            error.hidden = false;
            error.textContent = confirmed || uncertain ? this.dataset.uncertainLabel : message;
            if (confirmed || uncertain) {
              button.disabled = true;
              form.dataset.coordinateUncertain = 'true';
            }
          }
        }
        if (!confirmed && rendered) {
          const current = drawer.querySelector(`[data-coordinate-item][data-variant-id="${id}"]`);
          const currentError = current?.querySelector('[data-coordinate-error]');
          if (currentError) { currentError.textContent = message; currentError.hidden = false; }
        }
        if (offer && this.isConnected) {
          if (confirmed && rendered) {
            this.dispatchEvent(new CustomEvent('bellomo:coordinate-added', { bubbles: true }));
          } else {
            error.hidden = false;
            error.textContent = confirmed || uncertain ? this.dataset.uncertainLabel : message;
            if (confirmed || uncertain) {
              button.disabled = true;
              form.dataset.coordinateUncertain = 'true';
            }
          }
        }
      }
    }
  }

  customElements.define('bellomo-cart-coordinates', BellomoCartCoordinates);
})();
